/**
 * Campaign Map — DOM+SVG rendering of the campaign graph.
 * Full-screen overlay, left-to-right DAG, localStorage persistence.
 */
import type { CampaignState, CampaignNode, CampaignSaveData, CampaignUnit, WeaponKey, ItemKey } from '../types';
import { serializeCampaign, deserializeCampaign } from '../core/campaign';
import type { CampaignResult } from '../core/campaign';
import { MODE_DISPLAY_NAMES, MAX_MISSION_UNITS, TEAMS } from '../constants';
import { fx, cur } from './flavor';
import { theme } from './theme';

const STORAGE_KEY = 'orbituary-campaign';

// =============================================================================
// MODULE STATE
// =============================================================================

let overlayEl: HTMLDivElement | null = null;
let svgEl: SVGSVGElement | null = null;
let graphArea: HTMLDivElement | null = null;
let nodesContainer: HTMLDivElement | null = null;
let previewPanel: HTMLDivElement | null = null;
let headerEl: HTMLDivElement | null = null;
let advanceBtn: HTMLButtonElement | null = null;

// Cached layout dimensions — locked on first render so preview panel toggle doesn't shift nodes
let cachedLayout: { W: number; H: number } | null = null;

let restartBtn: HTMLButtonElement | null = null;
let inspectFlagEl: HTMLDivElement | null = null;
let restartConfirmTimeout: ReturnType<typeof setTimeout> | null = null;

// Mission team selection state
let selectedMissionUnits: Set<number> = new Set([0]); // commander always selected
let currentCampaignState: CampaignState | null = null;

let currentCallbacks: {
  onDeploy: (nodeId: number, selectedUnitIds: number[]) => void;
  onAdvanceWave: () => void;
  onBack: () => void;
  onRestart: () => void;
  onStore?: () => void;
  onTeam?: () => void;
  onGear?: () => void;
} | null = null;

// =============================================================================
// SHOW / HIDE
// =============================================================================

export function showCampaignMap(
  state: CampaignState,
  callbacks: {
    onDeploy: (nodeId: number, selectedUnitIds: number[]) => void;
    onAdvanceWave: () => void;
    onBack: () => void;
    onRestart: () => void;
    onStore?: () => void;
    onTeam?: () => void;
    onGear?: () => void;
  }
): void {
  currentCallbacks = callbacks;
  currentCampaignState = state;

  if (!overlayEl) {
    createOverlay();
  }

  overlayEl!.style.display = 'flex';
  updateCampaignMap(state);
}

export function updateCampaignMap(state: CampaignState): void {
  if (!overlayEl) return;
  currentCampaignState = state;
  renderHeader(state);
  renderGraph(state);
  renderAdvanceButton(state);
}

export function hideCampaignMap(): void {
  if (overlayEl) {
    overlayEl.style.display = 'none';
  }
  hideBattlePreview();
  resetRestartButton();
  cachedLayout = null;
}

export function showBattlePreview(node: CampaignNode, roster: CampaignUnit[], onDeploy: (selectedUnitIds: number[]) => void): void {
  if (!previewPanel) return;

  const cmdUnit = roster.find(u => u.id === 0);
  const commanderHasAp = cmdUnit != null && cmdUnit.ap > 0;
  const botDriverMode = !commanderHasAp;

  // Reset selection: commander included only if they have AP
  selectedMissionUnits = botDriverMode ? new Set() : new Set([0]);

  const config = node.battleConfig;
  const diffColor = config.botDifficulty === 'easy' ? theme.difficulty.easy
    : config.botDifficulty === 'medium' ? theme.difficulty.medium
    : config.botDifficulty === 'hard' ? theme.difficulty.hard
    : theme.difficulty.expert;

  const aliveRosterUnits = roster.filter(u => u.alive && u.id > 0);
  // In bot-driver mode all slots are for bots; otherwise commander takes one slot
  const maxNonCmd = botDriverMode ? MAX_MISSION_UNITS : MAX_MISSION_UNITS - 1;

  // Build panel content
  previewPanel.innerHTML = `
    <div style="font-size:14px; color:${theme.accent}; margin-bottom:8px; font-weight:600;">${node.name}</div>
    <div style="font-size:11px; color:${theme.chip.text}; margin-bottom:4px;">Layer ${node.layer + 1}</div>
    <div style="font-size:11px; margin-bottom:12px;">
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Mode:</span> <span style="color:${theme.text.primary};">${MODE_DISPLAY_NAMES[config.gameMode] ?? config.gameMode}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Difficulty:</span> <span style="color:${diffColor};">${config.botDifficulty}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Planets:</span> <span style="color:${theme.text.primary};">${config.numPlanets}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Teams:</span> <span style="color:${theme.text.primary};">${config.numTeams}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Units/team:</span> <span style="color:${theme.text.primary};">${config.botUnitsPerTeam}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Reward:</span> <span style="color:${theme.accent};">${config.reward} ${cur()}</span></div>
      <div><span style="color:${theme.text.secondary};">Income:</span> <span style="color:${theme.positive};">+${config.incomePerWave} ${cur()}/wave</span></div>
      ${config.recruitUnit ? `<div style="margin-top:6px; padding:4px 8px; background:${theme.goldBg10}; border:1px solid ${theme.surface.border}; border-radius:4px;"><span style="color:${theme.accent}; font-weight:600; font-size:11px;">${fx('RECRUIT AVAILABLE', 'RECRUITABLE CONTRACTOR')}</span></div>` : ''}
    </div>
    <div style="
      background:${theme.systemBoxBg}; border:1px solid ${TEAMS[0].primary}33;
      border-radius:6px; padding:8px 10px; margin-bottom:12px;
    ">
      <div style="display:flex; align-items:center; gap:6px;">
        <svg width="12" height="16" viewBox="0 0 14 18" fill="none">
          <rect x="1" y="0" width="2" height="18" fill="${TEAMS[0].primary}" opacity="0.8"/>
          <path d="M3 1 L13 4 L3 7 Z" fill="${TEAMS[0].primary}" opacity="0.9"/>
        </svg>
        <span style="color:${TEAMS[0].primary}; font-size:11px; font-weight:600; letter-spacing:1px;">UNOWNED SYSTEM</span>
      </div>
    </div>
    <div id="campaign-preview-canvas-container" style="
      display:flex; justify-content:center; margin-bottom:14px;
      background:${theme.surface.canvasBg}; border-radius:50%; width:180px; height:180px;
      margin-left:auto; margin-right:auto; border:1px solid ${theme.goldBorder15};
    "></div>
    <div id="campaign-team-selector" style="margin-bottom:14px;">
      <div style="font-size:11px; color:${theme.accent}; margin-bottom:8px; font-weight:600; letter-spacing:1px;">MISSION TEAM</div>
      <div id="campaign-team-rows" style="display:flex; flex-direction:column; gap:4px;"></div>
    </div>
    <button id="campaign-deploy-btn" style="
      width:100%; padding:10px; border:1px solid ${theme.accent}; background:${theme.goldBorder15};
      color:${theme.accent}; font-family:'Space Grotesk',monospace; font-size:13px; cursor:pointer;
      border-radius:6px; font-weight:600; letter-spacing:1px;
    ">DEPLOY</button>
  `;

  // Render large system preview into the container
  const canvasContainer = document.getElementById('campaign-preview-canvas-container');
  if (canvasContainer) {
    const largeCanvas = renderSystemCanvas(node, 180);
    largeCanvas.style.cssText = 'pointer-events:none; border-radius:50%;';
    canvasContainer.appendChild(largeCanvas);
  }

  // Build team selector rows
  const teamRows = document.getElementById('campaign-team-rows');
  if (teamRows) {
    if (botDriverMode) {
      // Bot-driver mode label instead of commander row
      const driverLabel = document.createElement('div');
      driverLabel.style.cssText = `display:flex; align-items:center; gap:6px; padding:4px 6px; background:${theme.goldBg08}; border-radius:4px;`;
      driverLabel.innerHTML = `
        <span style="color:${theme.accent}; font-size:11px; font-weight:600; letter-spacing:1px;">BOT DRIVER MODE</span>
      `;
      teamRows.appendChild(driverLabel);
    } else {
      // Commander row (always selected, non-toggleable)
      const cmdApColor = cmdUnit && cmdUnit.ap > 0 ? theme.positive : theme.negative;
      const cmdRow = document.createElement('div');
      cmdRow.style.cssText = `display:flex; align-items:center; gap:6px; padding:4px 6px; background:${theme.goldBg08}; border-radius:4px;`;
      cmdRow.innerHTML = `
        <span style="color:${theme.accent}; font-size:12px;">&#9745;</span>
        <span style="color:${theme.text.primary}; font-size:11px; flex:1;">${cmdUnit?.name ?? 'Commander'}</span>
        <span style="font-size:9px; color:${cmdApColor}; padding:1px 4px;">${cmdUnit?.ap ?? 0}/${cmdUnit?.maxAp ?? 0}</span>
        <span style="font-size:9px; color:${theme.accent}; background:${theme.goldBorder15}; padding:1px 4px; border-radius:2px; font-weight:600;">CMD</span>
      `;
      teamRows.appendChild(cmdRow);
    }

    // Roster unit rows
    for (const unit of aliveRosterUnits) {
      const exhausted = unit.ap <= 0;
      const row = document.createElement('div');
      row.dataset.unitId = String(unit.id);
      row.style.cssText = `display:flex; align-items:center; gap:6px; padding:4px 6px; border-radius:4px; transition:opacity 0.15s; ${exhausted ? 'cursor:default;' : 'cursor:pointer;'}`;
      updateTeamRow(row, unit, false, exhausted ? true : false);

      if (!exhausted) {
        row.addEventListener('click', () => {
          const isSelected = selectedMissionUnits.has(unit.id);
          if (isSelected) {
            selectedMissionUnits.delete(unit.id);
          } else {
            // Check if at max
            const cmdInSet = selectedMissionUnits.has(0);
            const nonCmdCount = selectedMissionUnits.size - (cmdInSet ? 1 : 0);
            if (nonCmdCount >= maxNonCmd) return; // can't add more
            selectedMissionUnits.add(unit.id);
          }
          // Update all rows
          refreshTeamRows(teamRows, aliveRosterUnits, maxNonCmd);
        });
      }

      teamRows.appendChild(row);
    }
  }

  previewPanel.style.display = 'block';
  showInspectFlag(node.id);

  const deployBtn = document.getElementById('campaign-deploy-btn') as HTMLButtonElement | null;
  if (deployBtn) {
    // In bot-driver mode, disable until at least 1 unit selected
    if (botDriverMode && selectedMissionUnits.size === 0) {
      deployBtn.disabled = true;
      deployBtn.style.opacity = '0.4';
    }
    deployBtn.addEventListener('click', () => {
      if (deployBtn.disabled) return;
      onDeploy(Array.from(selectedMissionUnits));
    });
  }

  // Store references for refresh to update deploy button state
  const deployBtnRef = deployBtn;
  // Wrap refreshTeamRows calls to also update deploy button
  if (teamRows && botDriverMode && deployBtnRef) {
    const observer = new MutationObserver(() => {
      const hasUnits = selectedMissionUnits.size > 0;
      deployBtnRef.disabled = !hasUnits;
      deployBtnRef.style.opacity = hasUnits ? '1' : '0.4';
    });
    observer.observe(teamRows, { childList: true, subtree: true, attributes: true });
  }
}

function updateTeamRow(row: HTMLDivElement, unit: CampaignUnit, selected: boolean, dimmed: boolean): void {
  const exhausted = unit.ap <= 0;
  const checkbox = exhausted ? '&#10005;' : selected ? '&#9745;' : '&#9744;';
  const checkColor = exhausted ? theme.negative : selected ? theme.positive : theme.text.muted;
  const nameColor = dimmed ? theme.text.disabled : theme.text.primary;
  const opacity = dimmed ? '0.5' : '1';
  const apColor = unit.ap > 0 ? theme.positive : theme.negative;
  row.style.opacity = opacity;
  row.style.background = selected ? theme.selectedRowBg : 'transparent';
  row.innerHTML = `
    <span style="color:${checkColor}; font-size:12px;">${checkbox}</span>
    <span style="color:${nameColor}; font-size:11px; flex:1;">${unit.name}</span>
    <span style="font-size:9px; color:${apColor}; padding:1px 4px;">${unit.ap}/${unit.maxAp}</span>
    <span style="font-size:9px; color:${theme.text.secondary}; background:${theme.chipBg}; padding:1px 4px; border-radius:2px;">${unit.skillKey}</span>
  `;
}

function refreshTeamRows(container: HTMLElement, units: CampaignUnit[], maxNonCmd: number): void {
  // In bot-driver mode commander isn't in the set, so all selected are non-cmd
  const cmdInSet = selectedMissionUnits.has(0);
  const nonCmdCount = selectedMissionUnits.size - (cmdInSet ? 1 : 0);
  const atMax = nonCmdCount >= maxNonCmd;
  for (const unit of units) {
    const row = container.querySelector(`[data-unit-id="${unit.id}"]`) as HTMLDivElement | null;
    if (!row) continue;
    const selected = selectedMissionUnits.has(unit.id);
    const dimmed = !selected && atMax;
    updateTeamRow(row, unit, selected, dimmed);
  }
}

export function hideBattlePreview(): void {
  removeInspectFlag();
  if (previewPanel) {
    previewPanel.style.display = 'none';
    previewPanel.innerHTML = '';
  }
}

function removeInspectFlag(): void {
  if (inspectFlagEl) {
    inspectFlagEl.remove();
    inspectFlagEl = null;
  }
}

function showInspectFlag(nodeId: number): void {
  removeInspectFlag();
  const nodeEl = nodesContainer?.querySelector(`[data-node-id="${nodeId}"]`);
  if (!nodeEl) return;
  const teamColor = TEAMS[0].primary;
  inspectFlagEl = document.createElement('div');
  inspectFlagEl.style.cssText = `
    position:absolute; top:-2px; right:-2px; pointer-events:none;
    width:14px; height:18px; animation: campaign-flag-blink 2.5s ease-in-out infinite;
  `;
  inspectFlagEl.innerHTML = `<svg width="14" height="18" viewBox="0 0 14 18" fill="none">
    <rect x="1" y="0" width="2" height="18" fill="${teamColor}" opacity="0.8"/>
    <path d="M3 1 L13 4 L3 7 Z" fill="${teamColor}" opacity="0.9"/>
  </svg>`;
  nodeEl.appendChild(inspectFlagEl);
}

function showSystemInspect(node: CampaignNode): void {
  if (!previewPanel) return;

  const config = node.battleConfig;
  const teamColor = TEAMS[0].primary;
  const teamName = TEAMS[0].name;

  previewPanel.innerHTML = `
    <div style="font-size:14px; color:${theme.accent}; margin-bottom:8px; font-weight:600;">${node.name}</div>
    <div style="font-size:11px; color:${theme.chip.text}; margin-bottom:12px;">Layer ${node.layer + 1}</div>
    <div id="inspect-preview-canvas" style="
      display:flex; justify-content:center; margin-bottom:14px;
      background:${theme.surface.canvasBg}; border-radius:50%; width:180px; height:180px;
      margin-left:auto; margin-right:auto; border:1px solid ${theme.goldBorder15};
    "></div>
    <div style="
      background:${theme.systemBoxBgOwned}; border:1px solid ${teamColor}44;
      border-radius:6px; padding:10px 12px; margin-bottom:14px;
    ">
      <div style="font-size:10px; color:${teamColor}; letter-spacing:1px; font-weight:600; margin-bottom:6px;">OWNED BY</div>
      <div style="display:flex; align-items:center; gap:6px;">
        <svg width="12" height="16" viewBox="0 0 14 18" fill="none">
          <rect x="1" y="0" width="2" height="18" fill="${teamColor}" opacity="0.8"/>
          <path d="M3 1 L13 4 L3 7 Z" fill="${teamColor}" opacity="0.9"/>
        </svg>
        <span style="color:${teamColor}; font-size:12px;">${teamName}</span>
      </div>
    </div>
    <div style="
      background:${theme.rowBg}; border:1px solid ${theme.surface.borderSubtle};
      border-radius:6px; padding:10px 12px; margin-bottom:14px;
    ">
      <div style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; font-weight:600; margin-bottom:8px;">SYSTEM REVENUE</div>
      ${(() => {
        const healthMult = (currentCampaignState?.nodeHealthMultipliers ?? {})[node.id] ?? 1.0;
        const effectiveIncome = Math.round(config.incomePerWave * healthMult);
        const healthPct = Math.round(healthMult * 100);
        const healthColor = healthPct >= 80 ? theme.positive : healthPct >= 50 ? theme.warning : theme.negative;
        return `
          <div style="display:flex; justify-content:space-between; font-size:12px; margin-bottom:4px;">
            <span style="color:${theme.text.secondary};">System health</span>
            <span style="color:${healthColor};">${healthPct}%</span>
          </div>
          <div style="display:flex; justify-content:space-between; font-size:12px; margin-bottom:4px;">
            <span style="color:${theme.text.secondary};">Income per wave</span>
            <span style="color:${theme.positive};">+${effectiveIncome} ${cur()}</span>
            ${healthPct < 100 ? `<span style="color:${theme.text.muted}; font-size:10px; margin-left:4px;">(base ${config.incomePerWave})</span>` : ''}
          </div>`;
      })()}
      <div style="display:flex; justify-content:space-between; font-size:12px;">
        <span style="color:${theme.text.secondary};">Battle reward</span>
        <span style="color:${theme.accent};">${config.reward} ${cur()}</span>
      </div>
    </div>
    <div style="font-size:11px; color:${theme.text.muted}; line-height:1.5;">
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Mode:</span> <span style="color:${theme.chip.text};">${MODE_DISPLAY_NAMES[config.gameMode] ?? config.gameMode}</span></div>
      <div style="margin-bottom:4px;"><span style="color:${theme.text.secondary};">Planets:</span> <span style="color:${theme.chip.text};">${config.numPlanets}</span></div>
      <div><span style="color:${theme.text.secondary};">Teams:</span> <span style="color:${theme.chip.text};">${config.numTeams}</span></div>
    </div>
  `;

  const canvasContainer = document.getElementById('inspect-preview-canvas');
  if (canvasContainer) {
    const canvas = renderSystemCanvas(node, 180);
    canvas.style.cssText = 'pointer-events:none; border-radius:50%;';
    canvasContainer.appendChild(canvas);
  }

  previewPanel.style.display = 'block';
}

// =============================================================================
// CAMPAIGN END SCREEN
// =============================================================================

let endScreenEl: HTMLDivElement | null = null;

export function showCampaignEndScreen(
  result: CampaignResult,
  summary: { totalNodes: number; completedCount: number; lostCount: number; moneyEarned: number; layersCleared: number },
  callbacks: { onMenu: () => void; onRestart: () => void }
): void {
  hideCampaignMap();

  if (!endScreenEl) {
    endScreenEl = document.createElement('div');
    endScreenEl.id = 'campaign-end-screen';
    document.body.appendChild(endScreenEl);
  }

  const isWin = result.outcome === 'win';
  const accentColor = isWin ? theme.accent : theme.negativeStrong;
  const titleText = isWin ? fx('CAMPAIGN COMPLETE', 'BID CYCLE COMPLETE') : fx('CAMPAIGN OVER', 'BID CYCLE TERMINATED');
  const subtitleText = isWin
    ? fx('Every objective is complete.', 'All contracted objectives have been fulfilled.')
    : fx('Your force has been wiped out.', 'GCPPA oversight has revoked your operational license.');

  endScreenEl.style.cssText = `
    position:fixed; inset:0; z-index:110;
    background:${theme.surface.endScreenBg};
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    font-family:'Space Grotesk',monospace;
  `;

  endScreenEl.innerHTML = `
    <div style="text-align:center; max-width:480px; padding:24px;">
      <div style="font-size:24px; color:${accentColor}; font-weight:700; letter-spacing:3px; margin-bottom:12px;">
        ${titleText}
      </div>
      <div style="font-size:13px; color:${theme.chip.text}; margin-bottom:8px; line-height:1.5;">
        ${subtitleText}
      </div>
      <div style="font-size:11px; color:${theme.text.secondary}; margin-bottom:28px; font-style:italic;">
        ${result.reason}
      </div>

      <div style="
        background:${theme.rowBg}; border:1px solid ${theme.surface.borderSubtle};
        border-radius:10px; padding:18px 24px; margin-bottom:28px; text-align:left;
      ">
        <div style="font-size:10px; color:${theme.text.secondary}; letter-spacing:2px; margin-bottom:12px; font-weight:600;">
          ${fx('BATTLE REPORT', 'AFTER-ACTION REPORT')}
        </div>
        <div style="display:flex; flex-direction:column; gap:8px; font-size:12px;">
          <div style="display:flex; justify-content:space-between;">
            <span style="color:${theme.text.secondary};">Sectors Cleared</span>
            <span style="color:${accentColor};">${summary.completedCount} / ${summary.totalNodes}</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span style="color:${theme.text.secondary};">Sectors Lost</span>
            <span style="color:${theme.negative};">${summary.lostCount}</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span style="color:${theme.text.secondary};">Waves Survived</span>
            <span style="color:${theme.text.primary};">${summary.layersCleared}</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span style="color:${theme.text.secondary};">Final Budget</span>
            <span style="color:${theme.accent};">${summary.moneyEarned} ${cur()}</span>
          </div>
        </div>
      </div>

      <div style="display:flex; gap:12px; justify-content:center;">
        <button id="campaign-end-restart" style="
          padding:10px 24px; border:1px solid ${accentColor}; background:rgba(${isWin ? '212,175,55' : '196,80,80'},0.12);
          color:${accentColor}; font-family:'Space Grotesk',monospace; font-size:13px;
          cursor:pointer; border-radius:6px; font-weight:600; letter-spacing:1px;
        ">NEW CAMPAIGN</button>
        <button id="campaign-end-menu" style="
          padding:10px 24px; border:1px solid ${theme.text.secondary}; background:transparent;
          color:${theme.text.secondary}; font-family:'Space Grotesk',monospace; font-size:13px;
          cursor:pointer; border-radius:6px;
        ">MAIN MENU</button>
      </div>
    </div>
  `;

  document.getElementById('campaign-end-menu')?.addEventListener('click', () => {
    hideCampaignEndScreen();
    callbacks.onMenu();
  });
  document.getElementById('campaign-end-restart')?.addEventListener('click', () => {
    hideCampaignEndScreen();
    callbacks.onRestart();
  });
}

export function hideCampaignEndScreen(): void {
  if (endScreenEl) {
    endScreenEl.style.display = 'none';
    endScreenEl.innerHTML = '';
  }
}

// =============================================================================
// localStorage
// =============================================================================

export function saveCampaignToStorage(
  state: CampaignState,
  battleStash?: {
    battleStashAmmo: Partial<Record<WeaponKey, number>>;
    battleStashItems: Partial<Record<ItemKey, number>>;
    activeBattleNodeId: number;
  }
): void {
  const data = serializeCampaign(state);
  if (battleStash) {
    data.battleStashAmmo = battleStash.battleStashAmmo;
    data.battleStashItems = battleStash.battleStashItems;
    data.activeBattleNodeId = battleStash.activeBattleNodeId;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function loadCampaignFromStorage(): CampaignState | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const data: CampaignSaveData = JSON.parse(raw);
    const state = deserializeCampaign(data);
    // If there's a mid-battle stash, restore ammo/items to inventory
    // (player reloaded during a battle — give them their ammo back)
    if (data.battleStashAmmo || data.battleStashItems) {
      state.inventory = {
        ...state.inventory,
        ammoStock: data.battleStashAmmo ?? state.inventory.ammoStock,
        items: data.battleStashItems ?? state.inventory.items,
      };
      // Clear the stash from storage now that it's restored
      saveCampaignToStorage(state);
    }
    return state;
  } catch {
    return null;
  }
}

export function clearCampaignStorage(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// =============================================================================
// DOM CREATION
// =============================================================================

function createOverlay(): void {
  overlayEl = document.createElement('div');
  overlayEl.id = 'campaign-overlay';
  overlayEl.style.cssText = `
    position:fixed; inset:0; z-index:100;
    background:rgba(10,10,20,0.97);
    display:flex; flex-direction:column;
    font-family:'Space Grotesk',monospace;
    overflow:hidden;
  `;

  // Header
  headerEl = document.createElement('div');
  headerEl.style.cssText = `
    padding:12px 24px; display:flex; align-items:center; justify-content:space-between;
    border-bottom:1px solid ${theme.goldBorder15}; flex-shrink:0;
  `;
  overlayEl.appendChild(headerEl);

  // Main area (graph + preview)
  const mainArea = document.createElement('div');
  mainArea.style.cssText = 'flex:1; display:flex; position:relative; overflow:hidden; min-height:0;';

  // Graph area — this is the positioning reference for nodes + SVG
  graphArea = document.createElement('div');
  graphArea.style.cssText = 'flex:1; position:relative; min-width:0; overflow:auto;';
  graphArea.addEventListener('click', (e) => {
    // Close preview when clicking empty space (not a node)
    if (e.target === graphArea || e.target === nodesContainer) {
      hideBattlePreview();
    }
  });

  svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svgEl.style.cssText = 'position:absolute; top:0; left:0; pointer-events:none;';
  graphArea.appendChild(svgEl);

  nodesContainer = document.createElement('div');
  nodesContainer.style.cssText = 'position:absolute; top:0; left:0;';
  graphArea.appendChild(nodesContainer);

  mainArea.appendChild(graphArea);

  // Preview panel (right side)
  previewPanel = document.createElement('div');
  previewPanel.style.cssText = `
    width:220px; padding:16px; background:rgba(20,20,40,0.95);
    border-left:1px solid ${theme.goldBorder15};
    display:none; flex-shrink:0; overflow-y:auto;
  `;
  mainArea.appendChild(previewPanel);

  overlayEl.appendChild(mainArea);

  // Footer
  const footer = document.createElement('div');
  footer.style.cssText = `
    padding:10px 24px; display:flex; justify-content:space-between; align-items:center;
    border-top:1px solid ${theme.goldBorder15}; flex-shrink:0;
  `;

  const leftGroup = document.createElement('div');
  leftGroup.style.cssText = 'display:flex; gap:10px; align-items:center;';

  const backBtn = document.createElement('button');
  backBtn.textContent = 'BACK';
  backBtn.style.cssText = `
    padding:8px 20px; border:1px solid ${theme.text.secondary}; background:transparent;
    color:${theme.text.secondary}; font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px;
  `;
  backBtn.addEventListener('click', () => {
    if (currentCallbacks) currentCallbacks.onBack();
  });
  leftGroup.appendChild(backBtn);

  restartBtn = document.createElement('button');
  restartBtn.textContent = 'RESTART';
  restartBtn.style.cssText = `
    padding:8px 20px; border:1px solid rgba(196,80,80,0.4); background:transparent;
    color:rgba(196,80,80,0.6); font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px;
  `;
  restartBtn.addEventListener('click', () => {
    if (restartBtn!.dataset.confirm === 'true') {
      // Confirmed — do restart
      resetRestartButton();
      if (currentCallbacks) currentCallbacks.onRestart();
      return;
    }
    // First click — switch to confirm state
    restartBtn!.textContent = 'CONFIRM?';
    restartBtn!.style.borderColor = theme.negativeStrong;
    restartBtn!.style.color = theme.negativeStrong;
    restartBtn!.style.background = 'rgba(196,80,80,0.1)';
    restartBtn!.dataset.confirm = 'true';
    // Auto-revert after 3 seconds
    restartConfirmTimeout = setTimeout(resetRestartButton, 3000);
  });
  leftGroup.appendChild(restartBtn);

  footer.appendChild(leftGroup);

  // Center button group: STORE + TEAM
  const centerGroup = document.createElement('div');
  centerGroup.style.cssText = 'display:flex; gap:10px; align-items:center;';

  const storeBtn = document.createElement('button');
  storeBtn.textContent = 'STORE';
  storeBtn.style.cssText = `
    padding:8px 20px; border:1px solid ${theme.accent}; background:${theme.goldBg12};
    color:${theme.accent}; font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px; font-weight:600; letter-spacing:1px;
  `;
  storeBtn.addEventListener('click', () => {
    if (currentCallbacks?.onStore) currentCallbacks.onStore();
  });
  centerGroup.appendChild(storeBtn);

  const teamBtn = document.createElement('button');
  teamBtn.textContent = 'TEAM';
  teamBtn.style.cssText = `
    padding:8px 20px; border:1px solid ${theme.accent}; background:${theme.goldBg12};
    color:${theme.accent}; font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px; font-weight:600; letter-spacing:1px;
  `;
  teamBtn.addEventListener('click', () => {
    if (currentCallbacks?.onTeam) currentCallbacks.onTeam();
  });
  centerGroup.appendChild(teamBtn);

  const gearBtn = document.createElement('button');
  gearBtn.textContent = 'GEAR';
  gearBtn.style.cssText = `
    padding:8px 20px; border:1px solid ${theme.accent}; background:${theme.goldBg12};
    color:${theme.accent}; font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px; font-weight:600; letter-spacing:1px;
  `;
  gearBtn.addEventListener('click', () => {
    if (currentCallbacks?.onGear) currentCallbacks.onGear();
  });
  centerGroup.appendChild(gearBtn);

  footer.appendChild(centerGroup);

  advanceBtn = document.createElement('button');
  advanceBtn.textContent = 'ADVANCE WAVE';
  advanceBtn.style.cssText = `
    padding:8px 20px; border:1px solid ${theme.accent}; background:${theme.goldBorder15};
    color:${theme.accent}; font-family:'Space Grotesk',monospace; font-size:12px;
    cursor:pointer; border-radius:4px; font-weight:600;
  `;
  advanceBtn.addEventListener('click', () => {
    if (currentCallbacks) currentCallbacks.onAdvanceWave();
  });
  footer.appendChild(advanceBtn);

  overlayEl.appendChild(footer);
  document.body.appendChild(overlayEl);
}

// =============================================================================
// RENDERING
// =============================================================================

function resetRestartButton(): void {
  if (restartConfirmTimeout) {
    clearTimeout(restartConfirmTimeout);
    restartConfirmTimeout = null;
  }
  if (restartBtn) {
    restartBtn.textContent = 'RESTART';
    restartBtn.style.borderColor = 'rgba(196,80,80,0.4)';
    restartBtn.style.color = 'rgba(196,80,80,0.6)';
    restartBtn.style.background = 'transparent';
    delete restartBtn.dataset.confirm;
  }
}

function renderHeader(state: CampaignState): void {
  if (!headerEl) return;

  // Compute projected income from completed nodes (scaled by system health)
  const healthMults = state.nodeHealthMultipliers ?? {};
  let projectedIncome = 0;
  for (const nodeId of state.completedNodes) {
    const node = state.nodes.find(n => n.id === nodeId);
    if (node) {
      const mult = healthMults[nodeId] ?? 1.0;
      projectedIncome += Math.round(node.battleConfig.incomePerWave * mult);
    }
  }

  // Sum AP across all alive roster units
  const totalAp = state.roster.reduce((sum, u) => u.alive ? sum + u.ap : sum, 0);
  const totalMaxAp = state.roster.reduce((sum, u) => u.alive ? sum + u.maxAp : sum, 0);

  headerEl.innerHTML = `
    <div style="font-size:16px; color:${theme.accent}; font-weight:600; letter-spacing:2px;">
      ${fx('CAMPAIGN', 'BID CYCLE CAMPAIGN')}
    </div>
    <div style="display:flex; gap:24px; align-items:center;">
      <div style="color:${theme.chip.text}; font-size:12px;">
        Wave <span style="color:${theme.text.primary};">${(state.waveNumber ?? 0) + 1}</span>
      </div>
      <div style="color:${theme.chip.text}; font-size:12px;">
        AP <span style="color:${totalAp > 0 ? theme.positive : theme.negative};">${totalAp}/${totalMaxAp}</span>
      </div>
      <div style="color:${theme.chip.text}; font-size:12px;">
        Budget <span style="color:${theme.accent};">${state.playerMoney} ${cur()}</span>
      </div>
      ${projectedIncome > 0 ? `<div style="color:${theme.chip.text}; font-size:12px;">
        Income <span style="color:${theme.positive};">+${projectedIncome}/wave</span>
      </div>` : ''}
    </div>
  `;
}

function renderAdvanceButton(state: CampaignState): void {
  if (!advanceBtn) return;
  const isLastLayer = state.currentLayer >= state.maxLayer;
  advanceBtn.style.display = isLastLayer ? 'none' : 'block';
  advanceBtn.disabled = false;
}

function renderGraph(state: CampaignState): void {
  if (!nodesContainer || !svgEl || !graphArea) return;

  nodesContainer.innerHTML = '';
  svgEl.innerHTML = '';

  // Compute viewport size (always fresh — graph area may resize)
  const viewRect = graphArea.getBoundingClientRect();
  if (viewRect.width === 0 || viewRect.height === 0) return;

  const NODE_SIZE = 64;
  const NODE_SPACING = 90; // min px between node centers to avoid overlap

  // Padding (fixed px)
  const padL = 60;
  const padR = 60;
  const padT = 50;
  const padB = 50;

  const numLayers = state.maxLayer + 1;

  // Find max nodes in any layer to determine required height
  const nodesPerLayer = new Map<number, number>();
  for (const node of state.nodes) {
    nodesPerLayer.set(node.layer, (nodesPerLayer.get(node.layer) ?? 0) + 1);
  }
  const maxNodesInLayer = Math.max(...nodesPerLayer.values(), 1);

  // Minimum content dimensions to prevent overlap
  const minLayerBandW = NODE_SPACING * 1.5; // each layer needs at least this width
  const minContentW = padL + padR + numLayers * minLayerBandW;
  const minContentH = padT + padB + maxNodesInLayer * NODE_SPACING;

  const W = Math.max(viewRect.width, minContentW);
  const H = Math.max(viewRect.height, minContentH);

  // Set content size (may exceed viewport → scrollable)
  svgEl.setAttribute('width', String(W));
  svgEl.setAttribute('height', String(H));
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  nodesContainer.style.width = W + 'px';
  nodesContainer.style.height = H + 'px';

  // Subgrid layout
  const usableW = W - padL - padR;
  const usableH = H - padT - padB;
  const layerBandW = usableW / numLayers;
  const subColW = layerBandW / 3;
  const subRowH = usableH / 5;

  // Compute traversable nodes: locked nodes reachable from completed nodes via edges
  const traversableIds = new Set<number>();
  const completedSet = state.completedNodes;
  // BFS from all completed nodes through forward and lateral edges
  const visited = new Set<number>(completedSet);
  const queue = [...completedSet];
  while (queue.length > 0) {
    const id = queue.shift()!;
    const n = state.nodes.find(nd => nd.id === id);
    if (!n) continue;
    for (const edgeId of n.edges) {
      if (visited.has(edgeId)) continue;
      visited.add(edgeId);
      const target = state.nodes.find(nd => nd.id === edgeId);
      if (!target) continue;
      if (target.status === 'locked' && target.layer > state.currentLayer) {
        traversableIds.add(edgeId);
      }
      // Continue BFS through completed nodes only (not through locked ones)
      if (completedSet.has(edgeId)) {
        queue.push(edgeId);
      }
    }
  }

  // Collect node positions (in pixels relative to container)
  const positions = new Map<number, { px: number; py: number }>();

  for (const node of state.nodes) {
    const layerOriginX = padL + node.layer * layerBandW;
    const px = layerOriginX + (node.subCol + 0.5) * subColW;
    const py = padT + (node.subRow + 0.5) * subRowH;

    positions.set(node.id, { px, py });

    const nodeEl = document.createElement('div');
    nodeEl.dataset.nodeId = String(node.id);
    nodeEl.style.cssText = `
      position:absolute;
      left:${px}px; top:${py}px;
      transform:translate(-50%,-50%);
      width:${NODE_SIZE}px; height:${NODE_SIZE}px;
      border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      cursor:pointer;
      transition: box-shadow 0.2s ease, border-color 0.2s ease;
    `;

    applyNodeStyle(nodeEl, node, traversableIds.has(node.id));
    nodeEl.title = `${node.name} — ${MODE_DISPLAY_NAMES[node.battleConfig.gameMode] ?? node.battleConfig.gameMode}`;

    // Canvas system preview — clipped to circle
    const canvasWrap = document.createElement('div');
    canvasWrap.style.cssText = `
      position:absolute; inset:0; border-radius:50%; overflow:hidden; pointer-events:none;
    `;
    const canvas = renderSystemCanvas(node, NODE_SIZE);
    canvas.style.cssText = 'pointer-events:none;';
    canvasWrap.appendChild(canvas);
    nodeEl.appendChild(canvasWrap);

    // Status overlay (checkmark / X for completed/lockedOut)
    const overlayLabel = getNodeOverlay(node);
    if (overlayLabel) {
      const badge = document.createElement('div');
      badge.style.cssText = `
        position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
        pointer-events:none; font-size:20px;
      `;
      badge.innerHTML = overlayLabel;
      nodeEl.appendChild(badge);
    }

    // Team ownership flag on completed nodes
    if (node.status === 'completed') {
      const teamColor = TEAMS[0].primary;
      const flag = document.createElement('div');
      flag.style.cssText = `
        position:absolute; top:-2px; right:-2px; pointer-events:none;
        width:14px; height:18px;
      `;
      flag.innerHTML = `<svg width="14" height="18" viewBox="0 0 14 18" fill="none">
        <rect x="1" y="0" width="2" height="18" fill="${teamColor}" opacity="0.8"/>
        <path d="M3 1 L13 4 L3 7 Z" fill="${teamColor}" opacity="0.9"/>
      </svg>`;
      nodeEl.appendChild(flag);
    }

    // Player position indicator
    if (node.id === state.playerNodeId) {
      const marker = document.createElement('div');
      marker.style.cssText = `
        position:absolute; top:-18px; left:50%; transform:translateX(-50%);
        font-size:9px; color:${theme.positive}; font-weight:700; letter-spacing:1px;
        white-space:nowrap; pointer-events:none;
        text-shadow: 0 0 6px rgba(136,196,160,0.6);
      `;
      marker.textContent = 'YOU';
      nodeEl.appendChild(marker);

      // Upward-pointing triangle below the label
      const arrow = document.createElement('div');
      arrow.style.cssText = `
        position:absolute; top:-6px; left:50%; transform:translateX(-50%);
        width:0; height:0;
        border-left:5px solid transparent; border-right:5px solid transparent;
        border-top:6px solid ${theme.positive};
        pointer-events:none; filter:drop-shadow(0 0 3px rgba(136,196,160,0.5));
      `;
      nodeEl.appendChild(arrow);
    }

    // Name below
    const nameEl = document.createElement('div');
    nameEl.style.cssText = `
      position:absolute; top:${NODE_SIZE + 4}px; left:50%; transform:translateX(-50%);
      white-space:nowrap; font-size:9px; color:${theme.text.secondary}; pointer-events:none;
    `;
    nameEl.textContent = node.name;
    nodeEl.appendChild(nameEl);

    if (node.status === 'available') {
      nodeEl.addEventListener('click', (e) => {
        e.stopPropagation();
        hideBattlePreview();
        const roster = currentCampaignState?.roster ?? [];
        showBattlePreview(node, roster, (selectedUnitIds) => {
          if (currentCallbacks) currentCallbacks.onDeploy(node.id, selectedUnitIds);
        });
      });
    } else if (node.status === 'completed') {
      nodeEl.style.cursor = 'pointer';
      nodeEl.addEventListener('click', (e) => {
        e.stopPropagation();
        hideBattlePreview();
        showSystemInspect(node);
      });
    }

    nodesContainer.appendChild(nodeEl);
  }

  // Draw SVG edges — track drawn lateral pairs to avoid duplicates
  const drawnLateral = new Set<string>();

  for (const node of state.nodes) {
    const from = positions.get(node.id);
    if (!from) continue;

    for (const targetId of node.edges) {
      const to = positions.get(targetId);
      if (!to) continue;

      const targetNode = state.nodes.find(n => n.id === targetId);
      if (!targetNode) continue;

      const isLateral = targetNode.layer === node.layer;

      // Skip duplicate lateral edges (only draw lower-id → higher-id)
      if (isLateral) {
        const key = Math.min(node.id, targetId) + '-' + Math.max(node.id, targetId);
        if (drawnLateral.has(key)) continue;
        drawnLateral.add(key);
      }

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(from.px));
      line.setAttribute('y1', String(from.py));
      line.setAttribute('x2', String(to.px));
      line.setAttribute('y2', String(to.py));

      const bothCompleted = state.completedNodes.has(node.id) && state.completedNodes.has(targetId);
      const oneCompletedOneAvailable = (state.completedNodes.has(node.id) && targetNode.status === 'available')
        || (state.completedNodes.has(targetId) && node.status === 'available');
      const oneCompletedOneTraversable = !oneCompletedOneAvailable && (
        (state.completedNodes.has(node.id) && traversableIds.has(targetId))
        || (state.completedNodes.has(targetId) && traversableIds.has(node.id))
      );
      // Edges in lockout zone not touching a completed node — fade them out
      const inLockedOut = (node.status === 'lockedOut' || targetNode.status === 'lockedOut')
        && !state.completedNodes.has(node.id) && !state.completedNodes.has(targetId);

      if (inLockedOut) {
        line.setAttribute('stroke', 'rgba(60,60,80,0.12)');
        line.setAttribute('stroke-width', '1');
        line.setAttribute('stroke-dasharray', '3,5');
      } else if (isLateral) {
        // Lateral edges: lighter, shorter dashes
        line.setAttribute('stroke', bothCompleted ? theme.accent : oneCompletedOneAvailable ? 'rgba(224,224,224,0.4)' : oneCompletedOneTraversable ? 'rgba(212,175,55,0.4)' : 'rgba(100,100,120,0.2)');
        line.setAttribute('stroke-width', '1');
        line.setAttribute('stroke-dasharray', '3,5');
      } else {
        // Forward edges
        line.setAttribute('stroke', bothCompleted ? theme.accent : oneCompletedOneAvailable ? 'rgba(224,224,224,0.5)' : oneCompletedOneTraversable ? 'rgba(212,175,55,0.45)' : 'rgba(100,100,120,0.3)');
        line.setAttribute('stroke-width', bothCompleted ? '2.5' : oneCompletedOneTraversable ? '1.5' : '1.5');
        if (!bothCompleted && !oneCompletedOneAvailable && !oneCompletedOneTraversable) {
          line.setAttribute('stroke-dasharray', '4,4');
        } else if (oneCompletedOneTraversable) {
          line.setAttribute('stroke-dasharray', '6,4');
        }
      }

      svgEl!.appendChild(line);
    }
  }

  // Lockout wave: dark overlay on locked-out layers + vertical indicator line
  if (state.currentLayer > 0 && state.maxLayer > 0) {
    // Wave line sits at the boundary between the locked-out band and the current band
    const waveX = padL + state.currentLayer * layerBandW;

    // Dark overlay behind the wave
    const rect2 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect2.setAttribute('x', '0');
    rect2.setAttribute('y', '0');
    rect2.setAttribute('width', String(waveX));
    rect2.setAttribute('height', String(H));
    rect2.setAttribute('fill', 'rgba(10,10,20,0.45)');
    svgEl.insertBefore(rect2, svgEl.firstChild);

    // Vertical wave indicator line
    const waveLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    waveLine.setAttribute('x1', String(waveX));
    waveLine.setAttribute('y1', '0');
    waveLine.setAttribute('x2', String(waveX));
    waveLine.setAttribute('y2', String(H));
    waveLine.setAttribute('stroke', 'rgba(196,80,80,0.5)');
    waveLine.setAttribute('stroke-width', '2');
    waveLine.setAttribute('stroke-dasharray', '8,6');
    svgEl.appendChild(waveLine);

    // "LOCKOUT" label on the wave line
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', String(waveX + 8));
    label.setAttribute('y', '20');
    label.setAttribute('fill', 'rgba(196,80,80,0.6)');
    label.setAttribute('font-size', '10');
    label.setAttribute('font-family', 'Space Grotesk, monospace');
    label.setAttribute('letter-spacing', '1');
    label.textContent = 'LOCKOUT';
    svgEl.appendChild(label);
  }
}

function applyNodeStyle(el: HTMLDivElement, node: CampaignNode, traversable = false): void {
  switch (node.status) {
    case 'available':
      el.style.border = '3px solid #e0e0e0';
      el.style.background = 'rgba(224,224,224,0.12)';
      el.style.color = '#e0e0e0';
      el.style.boxShadow = '0 0 20px rgba(224,224,224,0.4), 0 0 40px rgba(224,224,224,0.15), inset 0 0 12px rgba(224,224,224,0.1)';
      el.style.animation = 'campaign-pulse 2s ease-in-out infinite';
      el.style.outline = '2px solid rgba(224,224,224,0.35)';
      el.style.outlineOffset = '3px';
      break;
    case 'completed':
      el.style.border = `2px solid ${theme.accent}`;
      el.style.background = theme.surface.border;
      el.style.color = '#fff';
      el.style.boxShadow = '0 0 8px rgba(212,175,55,0.4)';
      break;
    case 'lost':
      el.style.border = `2px solid ${theme.negative}`;
      el.style.background = 'rgba(196,136,136,0.1)';
      el.style.color = theme.negative;
      break;
    case 'locked':
      if (traversable) {
        el.style.border = '2px solid rgba(212,175,55,0.6)';
        el.style.background = 'rgba(212,175,55,0.12)';
        el.style.color = 'rgba(212,175,55,0.7)';
        el.style.boxShadow = '0 0 10px rgba(212,175,55,0.25)';
      } else {
        el.style.border = '1px solid rgba(100,100,120,0.4)';
        el.style.background = 'rgba(40,40,60,0.3)';
        el.style.color = 'rgba(100,100,120,0.5)';
      }
      el.style.cursor = 'default';
      break;
    case 'lockedOut':
      el.style.border = '1px solid rgba(60,60,80,0.3)';
      el.style.background = 'rgba(20,20,30,0.5)';
      el.style.color = 'rgba(80,80,100,0.4)';
      el.style.cursor = 'default';
      el.style.textDecoration = 'line-through';
      break;
  }
}

export function renderSystemCanvas(node: Pick<CampaignNode, 'systemPreview'>, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = size + 'px';
  canvas.style.height = size + 'px';

  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  const bodies = node.systemPreview;
  if (bodies.length === 0) return canvas;

  // Find bounding box of all bodies
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const b of bodies) {
    minX = Math.min(minX, b.x - b.radius);
    maxX = Math.max(maxX, b.x + b.radius);
    minY = Math.min(minY, b.y - b.radius);
    maxY = Math.max(maxY, b.y + b.radius);
  }

  const bw = maxX - minX || 1;
  const bh = maxY - minY || 1;
  // Fit within inscribed circle of the square canvas (radius = size/2 - margin)
  const margin = 6;
  const inscribedR = size / 2 - margin;
  // Bodies must fit within a circle, so use the diagonal of the bounding box
  const bDiag = Math.sqrt(bw * bw + bh * bh) || 1;
  const scale = (inscribedR * 2) / bDiag;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  for (const b of bodies) {
    const sx = size / 2 + (b.x - cx) * scale;
    const sy = size / 2 + (b.y - cy) * scale;
    const sr = Math.max(b.radius * scale, 1.5);

    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);

    if (b.isStar) {
      // Radial glow for stars
      const grad = ctx.createRadialGradient(sx, sy, sr * 0.2, sx, sy, sr * 1.5);
      grad.addColorStop(0, b.color);
      grad.addColorStop(0.6, b.color);
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fill();
      // Bright core
      ctx.beginPath();
      ctx.arc(sx, sy, sr * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.globalAlpha = 0.6;
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      ctx.fillStyle = b.color;
      ctx.fill();
      // Subtle highlight
      ctx.beginPath();
      ctx.arc(sx - sr * 0.25, sy - sr * 0.25, sr * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fill();
    }
  }

  return canvas;
}

function getNodeOverlay(node: CampaignNode): string | null {
  if (node.status === 'completed') {
    return '<span style="font-size:22px; color:rgba(212,175,55,0.8); text-shadow:0 0 6px rgba(212,175,55,0.5);">&#10003;</span>';
  }
  if (node.status === 'lockedOut') {
    return '<span style="font-size:18px; color:rgba(100,100,120,0.6);">&#10005;</span>';
  }
  return null;
}

// Inject CSS animation for pulse
function injectStyles(): void {
  if (document.getElementById('campaign-styles')) return;
  const style = document.createElement('style');
  style.id = 'campaign-styles';
  style.textContent = `
    @keyframes campaign-flag-blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
    @keyframes campaign-pulse {
      0%, 100% { box-shadow: 0 0 20px rgba(224,224,224,0.35), 0 0 40px rgba(224,224,224,0.12), inset 0 0 12px rgba(224,224,224,0.08); outline-color: rgba(224,224,224,0.25); }
      50% { box-shadow: 0 0 28px rgba(224,224,224,0.6), 0 0 50px rgba(224,224,224,0.25), inset 0 0 16px rgba(224,224,224,0.15); outline-color: rgba(224,224,224,0.5); }
    }
  `;
  document.head.appendChild(style);
}

// Auto-inject on module load
injectStyles();
